import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  App,
  Button,
  Checkbox,
  InputNumber,
  Radio,
  Select,
  Table,
  Typography,
  message,
} from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { adminApi, formatApiError, type PageResult } from '../api'
import {
  AppFormModal,
  BROWSE_PICKER_Z_INDEX,
  OrgPersonBrowseField,
  OrgUnitBrowseField,
  RoleBrowseField,
  tablePagination,
  useServerPagination,
} from './ui'
import type { KnowledgeScope } from '../constants/knowledgeScope'
import {
  clampSharePermission,
  permissionOptionsForCap,
  type ShareGrantCap,
} from '../lib/shareGrantCap'
import './KnowledgeFolderShareRules.css'

type ShareRow = {
  id?: number
  row_key: string
  share_source: 'default' | 'manual'
  share_source_label: string
  deletable: boolean
  subject_type: string
  subject_type_label: string
  subject_id?: number | null
  subject_name: string
  sec_level_label: string
  permission_label: string
  downloadable_label: string
  added_by_name: string
}

type Props = {
  itemId: number | null
  folderId: number | null
  scope: KnowledgeScope
  editable?: boolean
  active?: boolean
}

const CREATOR_TYPES = [
  { value: 'creator_self', label: '创建人本人' },
  { value: 'creator_direct_supervisor', label: '创建人直接上级' },
  { value: 'creator_all_supervisors', label: '创建人所有上级' },
  { value: 'creator_same_department', label: '本部门' },
  { value: 'creator_same_position', label: '本岗位' },
  { value: 'creator_same_branch', label: '本分部' },
]

const GENERAL_TYPES = [
  { value: 'user', label: '人员' },
  { value: 'org_unit', label: '部门' },
  { value: 'role', label: '角色' },
  { value: 'everyone', label: '所有人' },
]

const BACKEND_SUPPORTED = new Set([
  ...CREATOR_TYPES.map((x) => x.value),
  'user',
  'org_unit',
  'role',
  'everyone',
])

const PICKER_TYPES = new Set(['user', 'org_unit', 'role'])

function labelOfSubject(type: string) {
  return [...CREATOR_TYPES, ...GENERAL_TYPES].find((x) => x.value === type)?.label || type
}

export function KnowledgeItemShareTab({ itemId, folderId, scope, editable = false, active = true }: Props) {
  const { modal } = App.useApp()
  const scopeParams = useMemo(() => ({ scope }), [scope])
  const { page, pageSize, handlePaginationChange, paginationOptions, resetPage } = useServerPagination()
  const [rows, setRows] = useState<ShareRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [selectedKeys, setSelectedKeys] = useState<number[]>([])
  const [addOpen, setAddOpen] = useState(false)
  const [saving, setSaving] = useState(false)

  const [categoryTab, setCategoryTab] = useState<'creator' | 'general'>('general')
  const [subjectType, setSubjectType] = useState('user')
  const [subjectId, setSubjectId] = useState<number | null>(null)
  const [subjectName, setSubjectName] = useState('')
  const [includeSubordinates, setIncludeSubordinates] = useState(false)
  const [includePartTime, setIncludePartTime] = useState(false)
  const [secMin, setSecMin] = useState(0)
  const [secMax, setSecMax] = useState(100)
  const [permission, setPermission] = useState<'view' | 'edit' | 'full'>('view')
  const [downloadable, setDownloadable] = useState(true)
  const [grantCap, setGrantCap] = useState<ShareGrantCap | null>(null)

  const permissionOptions = useMemo(() => permissionOptionsForCap(grantCap), [grantCap])

  useEffect(() => {
    if (!addOpen || !itemId || scope !== 'enterprise') {
      setGrantCap(null)
      return
    }
    adminApi
      .get<ShareGrantCap>(`/admin/knowledge/items/${itemId}/share-grant-cap`, { params: scopeParams })
      .then((r) => {
        setGrantCap(r.data)
        const clamped = clampSharePermission('view', true, r.data)
        setPermission(clamped.permission)
        setDownloadable(clamped.downloadable)
      })
      .catch(() => setGrantCap(null))
  }, [addOpen, itemId, scope, scopeParams])

  const resetForm = useCallback((category: 'creator' | 'general') => {
    if (category === 'creator') {
      setSubjectType('creator_self')
      setSubjectId(null)
      setSubjectName('创建人本人')
    } else {
      setSubjectType('user')
      setSubjectId(null)
      setSubjectName('')
    }
    setIncludeSubordinates(false)
    setIncludePartTime(false)
    setSecMin(0)
    setSecMax(100)
    const clamped = clampSharePermission('view', true, grantCap)
    setPermission(clamped.permission)
    setDownloadable(clamped.downloadable)
  }, [grantCap])

  const loadList = useCallback(() => {
    if (scope !== 'enterprise') return
    if (!itemId && !folderId) {
      setRows([])
      setTotal(0)
      return
    }
    setLoading(true)
    const req = itemId
      ? adminApi.get<PageResult<ShareRow>>(`/admin/knowledge/items/${itemId}/share-rules`, {
          params: { ...scopeParams, page, page_size: pageSize },
        })
      : adminApi.get<PageResult<ShareRow>>('/admin/knowledge/items/share-rules-preview', {
          params: { ...scopeParams, folder_id: folderId, page, page_size: pageSize },
        })
    req
      .then((r) => {
        const data = r.data
        setRows(
          (data.items || []).map((row) => ({
            ...row,
            row_key: row.row_key || (row.id != null ? `item:${row.id}` : `default:${row.subject_type}:${row.subject_id ?? ''}`),
          })),
        )
        setTotal(data.total ?? 0)
        setSelectedKeys([])
      })
      .catch((e) => message.error(formatApiError(e, '加载共享权限失败')))
      .finally(() => setLoading(false))
  }, [scope, itemId, folderId, scopeParams, page, pageSize])

  useEffect(() => {
    if (!active) return
    loadList()
  }, [active, loadList])

  const onCategoryChange = (category: 'creator' | 'general') => {
    setCategoryTab(category)
    resetForm(category)
  }

  const onSubjectTypeChange = (type: string) => {
    setSubjectType(type)
    if (categoryTab === 'creator') {
      setSubjectName(labelOfSubject(type))
      setSubjectId(null)
    } else if (PICKER_TYPES.has(type)) {
      setSubjectId(null)
      setSubjectName('')
    } else {
      setSubjectId(null)
      setSubjectName(labelOfSubject(type))
    }
  }

  const submitAdd = () => {
    if (!itemId) {
      message.info('请先保存文档后再添加文档级共享')
      return
    }
    if (!BACKEND_SUPPORTED.has(subjectType)) {
      message.info('该对象类型暂未支持')
      return
    }
    if (PICKER_TYPES.has(subjectType) && subjectId == null) {
      message.warning('请选择对象')
      return
    }
    if (secMin > secMax) {
      message.warning('安全级别范围不正确')
      return
    }
    const clamped = clampSharePermission(permission, downloadable, grantCap)
    if (clamped.permission !== permission || clamped.downloadable !== downloadable) {
      message.warning('权限不可高于您自身的文档权限')
      setPermission(clamped.permission)
      setDownloadable(clamped.downloadable)
      return
    }
    setSaving(true)
    adminApi
      .post(
        `/admin/knowledge/items/${itemId}/share-rules`,
        {
          subject_type: subjectType,
          subject_id: subjectId,
          category: categoryTab,
          permission: clamped.permission,
          downloadable: clamped.downloadable,
          sec_level_min: secMin,
          sec_level_max: secMax,
          include_subordinates: includeSubordinates,
          include_part_time: includePartTime,
        },
        { params: scopeParams },
      )
      .then(() => {
        message.success('共享权限已添加')
        setAddOpen(false)
        resetForm(categoryTab)
        resetPage()
        loadList()
      })
      .catch((e) => message.error(formatApiError(e, '添加共享权限失败')))
      .finally(() => setSaving(false))
  }

  const batchDelete = () => {
    if (!itemId || !selectedKeys.length) return
    modal.confirm({
      title: `删除选中的 ${selectedKeys.length} 条共享权限？`,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () =>
        adminApi
          .delete(`/admin/knowledge/items/${itemId}/share-rules`, {
            params: scopeParams,
            data: { rule_ids: selectedKeys },
          })
          .then(() => {
            message.success('已删除')
            resetPage()
            loadList()
          })
          .catch((e) => message.error(formatApiError(e, '删除失败'))),
    })
  }

  const showOrgOptions = categoryTab === 'general' && subjectType === 'org_unit'
  const showStaticObject =
    categoryTab === 'creator' || (categoryTab === 'general' && !PICKER_TYPES.has(subjectType))

  if (scope !== 'enterprise') {
    return <Typography.Text type="secondary">文档共享仅在企业知识库中可用。</Typography.Text>
  }

  if (!folderId && !itemId) {
    return <Typography.Text type="secondary">请先在「属性」中选择所属文件夹。</Typography.Text>
  }

  return (
    <div className="kf-doc-perm-shell kf-item-share-tab">
      <div className="kf-doc-perm-list-head">
        <span className="kf-doc-perm-list-title">共享列表</span>
        <span className="kf-doc-perm-list-tools">
          <Button
            type="text"
            icon={<PlusOutlined />}
            aria-label="新增"
            disabled={!editable || !itemId}
            onClick={() => {
              resetForm('general')
              setCategoryTab('general')
              setAddOpen(true)
            }}
          />
          <Button
            type="text"
            icon={<DeleteOutlined />}
            aria-label="批量删除"
            disabled={!editable || !itemId || !selectedKeys.length}
            onClick={batchDelete}
          />
        </span>
      </div>

      {!itemId ? (
        <Typography.Text type="secondary" className="kf-doc-perm-table-hint">
          以下为目录默认共享（保存文档后可追加文档级共享）
        </Typography.Text>
      ) : null}

      <Table<ShareRow>
        className="kf-doc-perm-table"
        rowKey="row_key"
        size="small"
        loading={loading}
        dataSource={rows}
        rowSelection={
          editable && itemId
            ? {
                selectedRowKeys: selectedKeys.map((id) => `item:${id}`),
                onChange: (_keys, selected) => {
                  setSelectedKeys(
                    selected.filter((r) => r.deletable && r.id != null).map((r) => r.id as number),
                  )
                },
                getCheckboxProps: (row) => ({ disabled: !row.deletable }),
              }
            : undefined
        }
        pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
        locale={{ emptyText: '暂无共享规则' }}
        columns={[
          { title: '共享类型', dataIndex: 'share_source_label', width: 96 },
          { title: '对象类型', dataIndex: 'subject_type_label', width: 120 },
          { title: '对象', dataIndex: 'subject_name', ellipsis: true },
          { title: '安全级别', dataIndex: 'sec_level_label', width: 96 },
          { title: '权限', dataIndex: 'permission_label', width: 96 },
          { title: '下载权限', dataIndex: 'downloadable_label', width: 96 },
          { title: '共享添加人', dataIndex: 'added_by_name', width: 120, ellipsis: true },
        ]}
      />

      <AppFormModal
        title="添加共享权限"
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={submitAdd}
        okText="确定"
        cancelText="取消"
        confirmLoading={saving}
        width={640}
        bodyHeight={380}
        zIndex={BROWSE_PICKER_Z_INDEX - 1}
        destroyOnHidden
      >
        <div className="kf-doc-perm-form-box" style={{ border: 'none', background: 'transparent', padding: 0 }}>
          <div className="kf-doc-perm-category-tabs">
            <button
              type="button"
              className={`kf-doc-perm-category-tab${categoryTab === 'creator' ? ' is-active' : ''}`}
              onClick={() => onCategoryChange('creator')}
            >
              与创建人相关
            </button>
            <button
              type="button"
              className={`kf-doc-perm-category-tab${categoryTab === 'general' ? ' is-active' : ''}`}
              onClick={() => onCategoryChange('general')}
            >
              通用
            </button>
          </div>

          <div className="kf-doc-perm-form-grid">
            <div className="kf-doc-perm-form-label">对象类型</div>
            <Radio.Group
              className="kf-doc-perm-radio-row"
              value={subjectType}
              onChange={(e) => onSubjectTypeChange(e.target.value)}
            >
              {(categoryTab === 'creator' ? CREATOR_TYPES : GENERAL_TYPES).map((opt) => (
                <Radio key={opt.value} value={opt.value}>
                  {opt.label}
                </Radio>
              ))}
            </Radio.Group>

            <div className="kf-doc-perm-form-label">对象</div>
            <div className="kf-doc-perm-object-field">
              {showStaticObject ? (
                <div className="kf-doc-perm-object-display is-static">
                  <span>{subjectName || labelOfSubject(subjectType)}</span>
                </div>
              ) : subjectType === 'user' ? (
                <OrgPersonBrowseField
                  value={
                    subjectId != null
                      ? { userId: subjectId, personId: 0, name: subjectName || `用户#${subjectId}` }
                      : null
                  }
                  onChange={(next) => {
                    setSubjectId(next?.userId ?? null)
                    setSubjectName(next?.name ?? '')
                  }}
                />
              ) : subjectType === 'org_unit' ? (
                <OrgUnitBrowseField
                  value={subjectId != null ? { id: subjectId, name: subjectName } : null}
                  onChange={(next) => {
                    setSubjectId(next?.id ?? null)
                    setSubjectName(next?.name ?? '')
                  }}
                />
              ) : (
                <RoleBrowseField
                  value={subjectId != null ? { id: subjectId, name: subjectName } : null}
                  onChange={(next) => {
                    setSubjectId(next?.id ?? null)
                    setSubjectName(next?.name ?? '')
                  }}
                />
              )}
            </div>

            {showOrgOptions ? (
              <>
                <div className="kf-doc-perm-form-label" />
                <div className="kf-doc-perm-check-row">
                  <Checkbox checked={includeSubordinates} onChange={(e) => setIncludeSubordinates(e.target.checked)}>
                    包含下级
                  </Checkbox>
                  <Checkbox checked={includePartTime} onChange={(e) => setIncludePartTime(e.target.checked)}>
                    包含兼职
                  </Checkbox>
                </div>
              </>
            ) : null}

            <div className="kf-doc-perm-form-label">安全级别</div>
            <div className="kf-doc-perm-sec-range">
              <InputNumber min={0} max={100} value={secMin} onChange={(v) => setSecMin(Number(v ?? 0))} />
              <span>-</span>
              <InputNumber min={0} max={100} value={secMax} onChange={(v) => setSecMax(Number(v ?? 100))} />
            </div>

            <div className="kf-doc-perm-form-label">权限</div>
            <div className="kf-doc-perm-perm-row">
              <Select
                value={permission}
                options={permissionOptions}
                onChange={(v) => {
                  const next = clampSharePermission(v, downloadable, grantCap)
                  setPermission(next.permission)
                  setDownloadable(next.downloadable)
                }}
              />
              <Checkbox
                checked={downloadable}
                disabled={permission !== 'view' || !grantCap?.allow_downloadable}
                onChange={(e) => setDownloadable(e.target.checked)}
              >
                可下载
              </Checkbox>
            </div>
          </div>
        </div>
      </AppFormModal>
    </div>
  )
}
